// ============================================================
// TERROR TCG — ONBOARDING: First Words (V2 — sentence-per-box)
// V'ael (Plaza matriarch) takes over from Willy mid-intro.
// One sentence = one dialogue box. ~70 phases.
//
// Speakers: Willy (left), V'ael (right), Player (centered nameplate,
// no stage figure of their own).
//
// Flow:
//   ACT I    — Willy alone, eager, on time, bumbling
//   ACT II   — V'ael arrives, interrupts sharp, breaks instantly
//              when Willy gives her the eyes; sends him off
//   ACT III  — V'ael's intro + J'ynxi-walked-off-the-cards lore
//   ACT IV   — Gender pick (interactive)
//   ACT V    — V'ael hands back to Willy and STRUTS OFFSTAGE
//   ACT VI   — Willy beats: silent stares, player breaks the
//              tension, sweet-treat negotiation, V'ael yells from
//              down the hall (distant flag — smaller, dimmer box)
//   ACT VII  — Willy's "Tealdiar" speed-run lore (Au Gratin, J'ynxi
//              plan, Solrend creation, seal weakened by 2 kids)
//   ACT VIII — Player reveals they're the kids' parents; Willy
//              accepts and offers a real tutorial at the front
//              help desk (deferred Plaza walkaround tour)
//   ACT IX   — Required mechanical picks (School + deck) wedged in
//              before finalize. The "real" tutorial moves to the
//              front-help-desk Plaza interaction post-onboarding.
//
// Special phase shapes:
//   silent  — auto-advances after SILENT_BEAT_MS (1600ms), no
//             nameplate, no continue button. Used for stage
//             directions (puppy-dog eyes, struts off, stares...).
//   distant — speaker is offstage; box shrinks + dims. Used for
//             V'ael's down-the-hall TLDR yell.
//
// Slots into Plaza.js before StarterPickerOverlay for brand-new
// users (profile has no starterDeckId AND no hasCompletedOnboarding).
// ============================================================

import { useState, useEffect, useCallback, useRef } from "react";
import { ref as fbRef, update as fbUpdate } from "firebase/database";
import { db } from "./firebase";
import { STARTER_DECKS, getStarterDeck } from "./starterDecks";
import StarterArt from "./StarterArt";   // the deck boxes — a player's first choice deserves a picture
import { addCardToCollection } from "./progression";
import { cdnUrl } from "./CDN";
import { VaelPortrait3D } from "./vaelModel3D"; // V'ael on the stage in 3D

// ── Grimoire Manuscript Palette (matches Plaza / FactionHall) ──
const GM = {
  page:          "#0f0c07",
  pageWarm:      "#1a1509",
  pageMid:       "#241c0c",
  pageLight:     "#2e2410",
  pageBorder:    "#3d2e14",
  inkGold:       "#c8963c",
  inkGoldBright: "#e8b84b",
  inkGoldDim:    "#8a6520",
  inkBrown:      "#8a6a30",
  inkRed:        "#8b2020",
  vellum:        "#e8d5a3",
  vellumDim:     "#b89a5a",
  vellumFaint:   "#7a6040",
  ruling:        "rgba(139,106,48,0.18)",
  font: "'Cinzel', 'Palatino Linotype', serif",
};

// ── Per-School accent palette ──────────────────────────────────
// ⚠ SPACE · PHANTOM · TIME · DRAGON ARE SCHOOLS — the TOP-RIGHT stamp.
// caP'n, 2026-09-14, asked outright: *"school :3"*. The Ilk is the
// TOP-LEFT stamp (Humanoid · Beast · Machine · Dragon) and no starter
// deck carries one. Every identifier in this file said Ilk until
// 2026-09-14, and the field it reads out of starterDecks.js is `school`
// now — see that file's banner for why all four readers moved together.
// THE PLAYER-FACING WORDS NEVER CHANGED: Willy says "Pick a family",
// the banner reads ◆ THE FOUR FAMILIES ◆, and the chips show the School
// word itself. No wrong noun ever reached a screen.
// Each family gets a signature accent tint for sigils, glow halos,
// and the School-reaction backdrop shift. Selected to feel distinct
// from each other and from GM gold so picks read at a glance.
const SCHOOL_ACCENT = {
  Space:   { tint: "#6aa6ff", glow: "#8cc4ff", label: "The Voyagers" },
  Phantom: { tint: "#b380ff", glow: "#d2b0ff", label: "The Half-There" },
  Time:    { tint: "#f0c060", glow: "#ffde8c", label: "The Already-Happened" },
  Dragon:  { tint: "#ff6a4a", glow: "#ff9a7a", label: "The Loud Ones" },
};

// ══════════════════════════════════════════════════════════════
// SIGILS — inline SVG, one per School. Simple, symbolic, legible.
// ══════════════════════════════════════════════════════════════
function SchoolSigil({ school, size = 60, color }) {
  const c = color || SCHOOL_ACCENT[school]?.tint || GM.inkGold;
  const s = size;
  if (school === "Space") {
    return (
      <svg width={s} height={s} viewBox="0 0 60 60" style={{ filter: `drop-shadow(0 0 8px ${c}88)` }}>
        <circle cx="30" cy="30" r="6" fill={c} opacity="0.9" />
        <ellipse cx="30" cy="30" rx="22" ry="8" stroke={c} strokeWidth="1.2" fill="none" opacity="0.7" />
        <ellipse cx="30" cy="30" rx="22" ry="8" stroke={c} strokeWidth="1.2" fill="none" opacity="0.5" transform="rotate(60 30 30)" />
        <ellipse cx="30" cy="30" rx="22" ry="8" stroke={c} strokeWidth="1.2" fill="none" opacity="0.5" transform="rotate(-60 30 30)" />
        <circle cx="52" cy="30" r="1.8" fill={c} />
        <circle cx="14" cy="18" r="1.2" fill={c} opacity="0.7" />
        <circle cx="46" cy="44" r="1.5" fill={c} opacity="0.8" />
      </svg>
    );
  }
  if (school === "Phantom") {
    return (
      <svg width={s} height={s} viewBox="0 0 60 60" style={{ filter: `drop-shadow(0 0 8px ${c}88)` }}>
        <ellipse cx="30" cy="30" rx="22" ry="13" stroke={c} strokeWidth="1.2" fill="none" opacity="0.6" />
        <ellipse cx="30" cy="30" rx="14" ry="8" fill={c} opacity="0.2" />
        <circle cx="30" cy="30" r="6" fill={c} opacity="0.95" />
        <circle cx="30" cy="30" r="2.5" fill={GM.page} />
        {/* Wispy trails */}
        <path d="M 10 20 Q 5 30 10 40" stroke={c} strokeWidth="1" fill="none" opacity="0.5" />
        <path d="M 50 20 Q 55 30 50 40" stroke={c} strokeWidth="1" fill="none" opacity="0.5" />
      </svg>
    );
  }
  if (school === "Time") {
    return (
      <svg width={s} height={s} viewBox="0 0 60 60" style={{ filter: `drop-shadow(0 0 8px ${c}88)` }}>
        <circle cx="30" cy="30" r="22" stroke={c} strokeWidth="1.5" fill="none" opacity="0.85" />
        {/* Broken segments — fragmented clock */}
        <path d="M 30 10 L 30 16" stroke={c} strokeWidth="2" />
        <path d="M 48 22 L 44 26" stroke={c} strokeWidth="2" />
        <path d="M 50 30 L 44 30" stroke={c} strokeWidth="2" opacity="0.4" />
        <path d="M 16 36 L 12 32" stroke={c} strokeWidth="2" />
        <path d="M 30 50 L 30 44" stroke={c} strokeWidth="2" opacity="0.6" />
        {/* Offset hands — one normal, one broken */}
        <line x1="30" y1="30" x2="38" y2="22" stroke={c} strokeWidth="2" />
        <line x1="30" y1="30" x2="22" y2="36" stroke={c} strokeWidth="1.5" opacity="0.5" strokeDasharray="2 2" />
        <circle cx="30" cy="30" r="2.5" fill={c} />
      </svg>
    );
  }
  if (school === "Dragon") {
    return (
      <svg width={s} height={s} viewBox="0 0 60 60" style={{ filter: `drop-shadow(0 0 8px ${c}88)` }}>
        {/* Wings spread */}
        <path d="M 30 32 Q 8 18 4 28 Q 12 30 18 32 Q 12 40 14 46 Q 22 38 30 36"
              fill={c} opacity="0.75" />
        <path d="M 30 32 Q 52 18 56 28 Q 48 30 42 32 Q 48 40 46 46 Q 38 38 30 36"
              fill={c} opacity="0.75" />
        {/* Body / head */}
        <ellipse cx="30" cy="34" rx="4" ry="8" fill={c} />
        <circle cx="30" cy="22" r="3.5" fill={c} />
        <circle cx="28.5" cy="21" r="0.8" fill={GM.page} />
        <circle cx="31.5" cy="21" r="0.8" fill={GM.page} />
      </svg>
    );
  }
  return null;
}

// ══════════════════════════════════════════════════════════════
// BACKDROP — pure-CSS starfield + drifting gold motes
// Atmospheric, layered, no art dependencies. Shifts subtly when
// a School is chosen to hint at the world responding.
// ══════════════════════════════════════════════════════════════
function Backdrop({ schoolAccent }) {
  const tint = schoolAccent?.tint || GM.inkGold;
  return (
    <div style={{
      position: "absolute", inset: 0, overflow: "hidden",
      background: `
        radial-gradient(ellipse at 20% 30%, ${tint}11, transparent 60%),
        radial-gradient(ellipse at 80% 70%, ${GM.inkGoldDim}22, transparent 55%),
        radial-gradient(ellipse at 50% 50%, ${GM.pageWarm}, ${GM.page} 75%)
      `,
      transition: "background 1.2s ease",
    }}>
      {/* Starfield — static layer */}
      <div className="ob-stars" />
      {/* Drifting gold motes — animated */}
      <div className="ob-motes" />
      {/* Fractured stone silhouettes hinted at bottom edges */}
      <div style={{
        position: "absolute", left: 0, right: 0, bottom: 0, height: "40%",
        background: `linear-gradient(to top, ${GM.page}cc, transparent)`,
        pointerEvents: "none",
      }} />
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// STAGE FIGURES — Willy + V'ael as full-height characters
// Real art on terror-assets CDN (via engine/CDN cdnUrl helper);
// SVG fallback if filename is null or .webp 404s.
// Active speaker is bright; inactive is dimmed.
// To use placeholder, set the slot to null.
// ══════════════════════════════════════════════════════════════
const NPC_PORTRAITS = {
  willy: "willy.webp",
  vael:  "vael.webp",
};
const npcUrl = (key) => NPC_PORTRAITS[key] ? cdnUrl(NPC_PORTRAITS[key]) : null;

// One figure on the stage. Sized to fill its half of the screen vertically.
// Placement is "left" or "right". Active state controls brightness/saturation.
function StageFigure({ who, side, active, fallbackInner }) {
  const [imgFailed, setImgFailed] = useState(false);
  // V'ael stands on this stage in 3D — her sculpted body, a dimension
  // higher than Willy's picture (caP'n, 2026-09-16). If the model fails
  // to load she drops back to the picture / plinth path below.
  const [bodyFailed, setBodyFailed] = useState(false);
  const url = npcUrl(who);
  const showBody = who === "vael" && !bodyFailed;
  const showImage = url && !imgFailed;

  // Figures sit anchored to the bottom of the screen, taking up most of
  // the vertical space. Side controls horizontal anchor. Inactive figure
  // dims and desaturates so the eye knows who's talking without losing
  // the visual presence of the other character.
  return (
    <div style={{
      position: "absolute",
      bottom: 0,
      [side]: 0,
      width: "50vw",
      height: "92vh",
      maxHeight: "92vh",
      display: "flex",
      alignItems: "flex-end",
      justifyContent: "center",
      pointerEvents: "none",
      transition: "opacity 0.6s ease, filter 0.6s ease, transform 0.6s ease",
      opacity: active ? 1 : 0.28,
      filter: active ? "none" : "grayscale(0.6) brightness(0.55)",
      transform: active ? "translateY(0)" : "translateY(8px)",
      zIndex: 1,
    }}>
      {showBody ? (
        <div style={{
          width: "100%", height: "100%",
          filter: active ? "drop-shadow(0 -10px 40px #d44a2a55)" : "none",
        }}>
          <VaelPortrait3D facing={{ x: -0.35, y: 1 }} onFail={() => setBodyFailed(true)} />
        </div>
      ) : showImage ? (
        <img
          src={url}
          alt={who}
          onError={() => setImgFailed(true)}
          style={{
            maxWidth: "100%",
            maxHeight: "100%",
            objectFit: "contain",
            objectPosition: "center bottom",
            display: "block",
            filter: active
              ? `drop-shadow(0 -10px 40px ${who === "vael" ? "#d44a2a55" : GM.inkGold + "55"})`
              : "none",
          }}
        />
      ) : (
        // Fallback inner (SVG-style placeholder) sized to fill the stage slot
        <div style={{
          width: "60%",
          height: "70%",
          background: who === "vael"
            ? `radial-gradient(ellipse at 50% 60%, #6a181088 0%, ${GM.page}00 75%)`
            : `linear-gradient(145deg, ${GM.pageMid}cc, ${GM.page}00)`,
          border: `2px solid ${who === "vael" ? "#d44a2a" : GM.inkGold + "88"}`,
          borderRadius: 12,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: who === "vael"
            ? `0 0 40px #d44a2a66, inset 0 0 40px #6a1810aa`
            : `0 0 32px ${GM.inkGold}44, inset 0 0 32px rgba(0,0,0,0.6)`,
        }}>
          {fallbackInner}
        </div>
      )}
    </div>
  );
}

// SVG fallback content for Willy — large italic W
function WillyFallback() {
  return (
    <div style={{
      fontSize: 220, fontWeight: 900, fontStyle: "italic",
      color: GM.inkGoldBright, fontFamily: GM.font,
      textShadow: `0 0 40px ${GM.inkGold}, 0 0 12px ${GM.inkGold}`,
    }}>W</div>
  );
}

// SVG fallback content for V'ael — large italic V with horns
function VaelFallback() {
  const ember = "#d44a2a";
  const emberBright = "#ff7a4a";
  return (
    <div style={{ position: "relative", display: "flex", flexDirection: "column", alignItems: "center" }}>
      <div style={{ display: "flex", gap: 40, marginBottom: -20 }}>
        <div style={{
          width: 0, height: 0,
          borderLeft: "16px solid transparent",
          borderRight: "16px solid transparent",
          borderBottom: `40px solid ${ember}cc`,
          transform: "rotate(-15deg)",
          filter: `drop-shadow(0 0 12px ${ember})`,
        }} />
        <div style={{
          width: 0, height: 0,
          borderLeft: "16px solid transparent",
          borderRight: "16px solid transparent",
          borderBottom: `40px solid ${ember}cc`,
          transform: "rotate(15deg)",
          filter: `drop-shadow(0 0 12px ${ember})`,
        }} />
      </div>
      <div style={{
        fontSize: 220, fontWeight: 900, fontStyle: "italic",
        color: emberBright, fontFamily: GM.font,
        textShadow: `0 0 40px ${ember}, 0 0 12px #fff`,
      }}>V</div>
    </div>
  );
}

// The stage — both figures present, active speaker highlighted.
// Willy on the left, V'ael on the right.
//
// Offstage flags fully hide a figure (used when V'ael "struts off" mid-script
// and the player should feel her absence, not just see her dimmed).
function SpeakerStage({ activeSpeaker, vaelOffstage = false, willyOffstage = false }) {
  return (
    <>
      {!willyOffstage && (
        <StageFigure
          who="willy"
          side="left"
          active={activeSpeaker === "willy"}
          fallbackInner={<WillyFallback />}
        />
      )}
      {!vaelOffstage && (
        <StageFigure
          who="vael"
          side="right"
          active={activeSpeaker === "vael"}
          fallbackInner={<VaelFallback />}
        />
      )}
    </>
  );
}

// ══════════════════════════════════════════════════════════════
// DIALOGUE BOX — wide bar across the bottom of the screen.
// Stage figures live separately (SpeakerStage component) above
// this box. A small nameplate floats above the box on the active
// character's side.
//
// Speakers: "willy" | "vael" | "player"
// Special flags on the phase (passed via props):
//   distant — speaker is offstage / down the hall. Box shrinks,
//             text dims, nameplate fades. Sells acoustic distance.
//   silent  — no nameplate, no continue button (auto-advance phase
//             handled by parent). Used for beat shots like "stares
//             at screen for a couple seconds."
// ══════════════════════════════════════════════════════════════
function DialogueBox({ lines, onContinue, children, continueLabel = "→", speaker = "willy", distant = false, silent = false }) {
  const VAEL_ACCENT = "#d44a2a";
  const PLAYER_ACCENT = "#7aa6c4"; // cool blue — distinct from both NPCs
  const PLAYER_ACCENT_BRIGHT = "#a6c8e0";

  let accent, accentBright, speakerLabel;
  if (speaker === "vael") {
    accent = VAEL_ACCENT;
    accentBright = "#ff7a4a";
    speakerLabel = "V'AEL";
  } else if (speaker === "player") {
    accent = PLAYER_ACCENT;
    accentBright = PLAYER_ACCENT_BRIGHT;
    speakerLabel = "YOU";
  } else {
    accent = GM.inkGold;
    accentBright = GM.inkGoldBright;
    speakerLabel = "WILLY";
  }
  // Nameplate placement: V'ael on right (her stage side), Willy on left (his),
  // Player centered (no stage figure of their own).
  let nameplateSide;
  if (speaker === "vael") nameplateSide = { right: "8%" };
  else if (speaker === "player") nameplateSide = { left: "50%", transform: "translateX(-50%)" };
  else nameplateSide = { left: "8%" };

  // "distant" makes the whole box smaller, text dimmer, nameplate ghosted.
  // Used for V'ael's "down the hall" T'yaeldyaer beat.
  const distantScale = distant ? 0.78 : 1;
  const distantOpacity = distant ? 0.55 : 1;

  return (
    <div style={{
      position: "absolute",
      left: "50%",
      bottom: "4vh",
      transform: `translateX(-50%) scale(${distantScale})`,
      transformOrigin: "bottom center",
      width: "min(92vw, 960px)",
      animation: "ob-slideUp 0.6s ease-out",
      opacity: distantOpacity,
      transition: "opacity 0.5s ease, transform 0.5s ease",
      zIndex: 3,
    }}>
      {/* Speaker nameplate — floats above the dialogue bar on the active side.
          Hidden entirely during silent beats. */}
      {!silent && (
        <div style={{
          position: "absolute",
          top: -22,
          ...nameplateSide,
          background: `linear-gradient(135deg, ${accent}33, ${GM.pageMid}ee)`,
          border: `1px solid ${accent}`,
          borderRadius: 3,
          padding: "4px 14px",
          fontSize: 11,
          letterSpacing: 5,
          fontWeight: 900,
          color: accentBright,
          fontFamily: GM.font,
          textShadow: `0 0 8px ${accent}`,
          boxShadow: `0 0 14px ${accent}55`,
          transition: "all 0.4s ease",
        }}>
          ◆ {speakerLabel} ◆
        </div>
      )}

      <div style={{
        background: `
          linear-gradient(145deg, ${GM.pageMid}f2, ${GM.pageWarm}f2),
          repeating-linear-gradient(0deg, transparent 0, transparent 26px, ${GM.ruling} 26px, ${GM.ruling} 27px)
        `,
        backgroundBlendMode: "multiply",
        border: `1px solid ${accent}88`,
        borderRadius: 4,
        padding: "20px 26px 18px",
        boxShadow: `0 0 40px rgba(0,0,0,0.7), 0 0 24px ${accent}22, inset 0 0 24px rgba(0,0,0,0.4)`,
        minHeight: 110,
        transition: "border-color 0.4s ease, box-shadow 0.4s ease",
      }}>
        {lines && lines.map((line, i) => (
          <p key={i} style={{
            margin: "0 0 8px 0",
            color: GM.vellum,
            fontSize: 16,
            lineHeight: 1.55,
            fontFamily: GM.font,
            fontStyle: line.italic ? "italic" : "normal",
            opacity: line.faint ? 0.7 : 1,
          }}>
            {line.text || line}
          </p>
        ))}
        {children}
        {onContinue && !silent && (
          <div style={{ textAlign: "right", marginTop: 10 }}>
            <button onClick={onContinue} style={{
              background: `linear-gradient(135deg, ${accent}33, ${GM.pageMid})`,
              border: `1px solid ${accent}`,
              borderRadius: 3, cursor: "pointer",
              color: accentBright, fontSize: 13,
              fontFamily: GM.font, fontWeight: 900,
              padding: "6px 18px", letterSpacing: 2,
              boxShadow: `0 0 12px ${accent}33`,
            }}>{continueLabel}</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// SCRIPT — the phases
// caP'n's V2 script: every sentence is its own dialogue box.
// Phase IDs follow the order of the script. Each phase returns a
// SINGLE-line array (one box per phase) unless the line is a paired
// thought caP'n explicitly wanted together.
//
// Special phase shapes:
//   { lines: [...] }                    standard speaker box
//   { lines: [...], silent: true }      auto-advances after a beat,
//                                        no nameplate, no continue button
//   { lines: [...], distant: true }     speaker is offstage / down the
//                                        hall, dialogue shrinks + dims
//
// Name interpolation: ${name} → playerName
// Speaker per phase is declared in PHASE_SPEAKER below the script.
// ══════════════════════════════════════════════════════════════

// Random goldfish-joke variants — picked once per session via getGoldfishJoke.
// Pulled out of the script so caP'n can edit / extend in one place.
const GOLDFISH_JOKES = [
  (n) => `"${n}. Good name. I boughted a goldfish called that once."`,
  (n) => `"${n}. Lovely name. Had a goldfish by that name. He didn't last long. Most of them don't."`,
  (n) => `"${n}. Mm. Good name for a goldfish. I'd know."`,
  (n) => `"${n}. Reminds me of a goldfish I had. Same name. Different vibe though."`,
  (n) => `"${n}. Ah, like the goldfish. The one I had. Brave little fellow. Until lunch."`,
];
function getGoldfishJoke(name) {
  const i = Math.floor(Math.random() * GOLDFISH_JOKES.length);
  return GOLDFISH_JOKES[i](name);
}

function getPhaseLines(phaseId, ctx) {
  const { playerName, chosenSchool, goldfishLine } = ctx;
  const name = playerName || "Aenarchist";

  const phases = {
    // ── ACT I — WILLY SOLO INTRO ────────────────────────────────
    // Willy is on time. V'ael is late. He starts the onboarding solo,
    // eager and bumbling, doing the boss's job because the boss isn't here.
    w_there_you_are:    [{ text: `"Ah. There are you"` }],
    w_started_thinking: [{ text: `"I was started to thinking you weren't to come. But here you have came. Good. Good good."` }],
    w_goldfish:         [{ text: goldfishLine || getGoldfishJoke(name) }],
    w_welcome_willy:    [{ text: `"Welcome, ${name}. I am Willy — I am free, I am me, I am we,lly. hehe..."` }],
    w_enough_games:     [{ text: `"Enough of your mind games! Come, Willy will really willfully will my way through the Wall of Will he or won't he and—"` }],
    w_where_was_i:      [{ text: `"Where was I? Oh yes, come, I will show you my plaza since my servant is not here. I'm doing it. Boss man. Acting boss. Acting man."` }],

    // ── ACT II — V'AEL ARRIVES, INTERRUPTS ──────────────────────
    v_acting_what:      [{ text: `"— acting WHAT."` }],
    v_your_who:         [{ text: `"Your who??"` }],
    v_my_plaza:         [{ text: `"This is MY Plaza. This is MY mission. This is MY game intro what makes YOU think you can—.."` }],

    // Beat shot — Willy gives her the puppy-dog eyes. No dialogue.
    // Auto-advances after a brief pause.
    beat_puppy_eyes: {
      lines: [{ text: `*Willy turns to her. Sad puppy-dog eyes. The big ones. The ones he uses.*`, italic: true, faint: true }],
      silent: true,
    },

    v_fine_boss_man:    [{ text: `"...Fine mr boss man, I'll let you wrap it up how bout dat?"` }],
    v_dungeon_without:  [{ text: `"This plaza would be a dungeon without him."`, italic: true }],

    // ── ACT III — V'AEL'S INTRO + STAKES ────────────────────────
    // ⚠ THE SEALED NAME IS NOT IN THIS SCRIPT AND MUST NOT COME BACK.
    // PRIME §2.8, caP'n: one mention, in the Rulebook, nowhere else,
    // ever. This file said it FIVE TIMES — twice in V'ael's mouth here
    // and three more in Willy's Tealdiar in ACT VII — which made it the
    // first proper noun a brand-new account ever hears, spent before
    // the player has drawn a card. Every joke, beat and rhythm below is
    // caP'n's and untouched; only the name became a pronoun or "the
    // condemned one", which is his own epithet from the Tealdiar two
    // acts down. The mystery is the point; nothing here is allowed to
    // spend it. Same law for Y'Tyranuz.
    v_look_at_me:       [{ text: `"You. ${name}. Look at me."` }],
    v_my_name:          [{ text: `"My name is V'ael. I am Lorde of this Emperium charged with the task of sealing back J'ynxi the Nefarious... yes she's real... she's not just a myth... and neither is the one who first sealed her."` }],
    v_pierre:           [{ text: `"My Lord, Pierre, has entrusted me with this task, and my Lord asks for so little, so when he does — I listen."`, italic: true }],
    v_meet_pierre:      [{ text: `"You may meet him eventually. Don't be cute about it."`, faint: true }],
    v_reason_here:      [{ text: `"There's a reason you're here. There's a reason any of this is here. Listen."` }],
    v_jynxi_name:       [{ text: `"J'ynxi. The Nefarious. You've heard the name — every Aenarchist hears the name eventually. She's not just on the cards."` }],
    v_walked_off:       [{ text: `"She walked off them. She's out there. Right now. Fractured — but not sealed. Running from every rift that might catch her."` }],
    v_call_of_duty:     [{ text: `"My duty — my CALL of duty — is to find her in this state and finish what the condemned one started."`, italic: true }],
    v_everything_serves:[{ text: `"Everything in this Plaza serves that. Every Solrend. Every Divine Crest ripped from your adversary's Soul. Every pack you crack. Every faction you pledge. It all feeds the seal."` }],
    v_thread:           [{ text: `"You don't have to care about that. But you're holding the thread now whether you care or not."`, faint: true }],

    // ── ACT IV — GENDER PICK ────────────────────────────────────
    v_before_further:   [{ text: `"Before we go any further. Which one of you is in charge?"` }],
    v_pick_no_agonize:  [{ text: `"Pick. Don't agonize."` }],
    // Interactive phase — gender picker renders here
    v_change_later: [
      { text: `"You can change later. Willy is patient. I am less so."`, italic: true },
    ],

    v_suits_you:        [{ text: `"Mm. Suits you."` }],
    v_now_the_rest:     [{ text: `"Now — the rest. How to play. The rules of the Solrend, the Shatterfield, the beings, the soul."` }],
    v_willys_time:      [{ text: `"That's Willy's time. I'm strategy and consequence. Letting him do it would be cruel to take from him."`, italic: true }],

    // ── ACT V — HAND-OFF TO WILLY (V'AEL EXITS) ─────────────────
    v_hand_back_willy:  [{ text: `"Willy. Sweet thing. You're up."` }],
    v_take_through:     [{ text: `"Take ${name} through the bones of it. The duel. The field. The beings. The soul. Everything you love explaining to me EVEN THOUGH I AM RANKED ONLY 2 AENARCHIST'S BEHIND YOU."` }],
    v_good_job:         [{ text: `"Good job. Now I'm upset. I'm gonna have a sweet treat with V'yen and you can't have any Mr. Boss Man."` }],
    // Beat — she struts off. No dialogue. (V'ael goes offstage from here.)
    beat_vael_struts: {
      lines: [{ text: `*she struts off down the hall.*`, italic: true, faint: true }],
      silent: true,
    },

    // ── ACT VI — WILLY ALONE BEAT ───────────────────────────────
    // Willy stares at the player for a couple seconds.
    beat_willy_stares: {
      lines: [{ text: `*Willy stares at the screen. Two beats. Three.*`, italic: true, faint: true }],
      silent: true,
    },
    beat_willy_dots1: {
      lines: [{ text: `"...."`, italic: true }],
      silent: true,
    },
    beat_willy_dots2: {
      lines: [{ text: `"...."`, italic: true }],
      silent: true,
    },

    // Player breaks the silence
    p_didnt_care:       [{ text: `"hmm looks like Willy didn't really care hu—?"` }],

    w_sweet_treat_want: [{ text: `*teary eyed* "Willy want sweet treat."`, italic: true }],
    w_make_fast:        [{ text: `"ok we make fast so Willy get sweet treat too. You come to front desk after if you want real tutorial. Fare?"` }],

    p_mmm_well:         [{ text: `"mmm well"` }],

    w_mmm_yes_treat:    [{ text: `"mmm yes sweet treat.. mmm. ok enough of your mind games already. This is the T'yaeldyaer."` }],

    // V'ael shouts back from down the hall — DISTANT (smaller, dimmer box)
    v_distant_tldr: {
      lines: [{ text: `*from far down the hall* "He means TLDR.... *loud groans of annoyance*"`, italic: true, faint: true },
      ],
      distant: true,
    },

    w_tealdiar_sorry:   [{ text: `"Ah. Yes. Tealdiar, I spelt wrong sorry. Sweet treat."` }],

    // ── ACT VII — WILLY'S TEALDIAR (the speed-run lore) ─────────
    w_au_gratin:        [{ text: `"This place Was the Au Gratin. Long story. Involves sweet treat."` }],
    w_unstable_world:   [{ text: `"Now this is the Unstable World."` }],
    w_xaol_sweet_treat: [{ text: `"The condemned one wanted sweet treat too..."` }],
    w_here_for_jynxi:   [{ text: `"but we are here for J'ynxi."` }],
    w_jynxi_plan:       [{ text: `"J'ynxi made a plan. Devious. Disguised as a duel. She called it a Solrend."` }],
    w_steal_sol:        [{ text: `"The idea was to steal his Sol, and in his fractured state, ruin him and take the condemned one's Divine Crests.... — all to remake the world in her laughter."` }],
    w_didnt_work:       [{ text: `"It didn't work, ho ho ho. He sealed her. Very easily, it was quite the universal joke. Ho ho."` }],
    w_terror_made:      [{ text: `"But — the deed was done, she had created Terror. The Solrend. We play it now. All time."` }],
    w_seal_weakened:    [{ text: `"and with that... sweet treat. She cheat. The rules bend to her, so over time, the seal weaked. 2 kids played, and now J'ynxi is watching over them—"` }],

    // ── ACT VIII — THE PARENTS REVEAL ──────────────────────────
    p_two_kids:         [{ text: `"yes 2 kids! that's what we're here for!"` }],
    w_who_are_yo:       [{ text: `"who are yo—"` }],
    p_were_parents:     [{ text: `"we're their parents!"` }],
    w_kings_grace:      [{ text: `"Ah. I see. So you don't need me to explain anymore then? I can sense both of yours' King's Grace after I mentioned your esclings. Good good."` }],
    p_find_in_plaza:    [{ text: `"We will be sure to find you in the plaza when we have more questions is that ok?"` }],
    w_yes_thank_you:    [{ text: `"yes thank you, very kind. I get you sweet treat. Meet me at front help desk and I will give you sweet treat."` }],
    p_sweet_treat:      [{ text: `"Sweet treat."` }],
    w_ah:               [{ text: `"Ah."` }],
    w_mmm:              [{ text: `"Mmm.."`, italic: true }],

    // ── ACT IX — DECK PICK (mechanically required for finalize) ─
    // The script doesn't have explicit dialogue for School + deck pick, but
    // finalize() can't write the profile without them. Wedged in just
    // before the curtain — keeping Willy laconic so it doesn't break
    // the sweet-treat tone.
    w_one_more_thing:   [{ text: `"Ah. Almost forgot. Pick a family. Then a deck. Quick like."` }],
    w_pick_family_prompt: [{ text: `"Four families, four flavors. Which one is yelling at you the loudest?"` }],
    // Interactive: School picker
    w_school_chosen:       [{ text: `"Mm. ${chosenSchool || "Good"}. Suits ya."` }],
    w_pick_deck_prompt: [{ text: `"Now the deck. Three of 'em. Pick the one that feels least like work."` }],
    // Interactive: deck picker
    w_deck_chosen:      [{ text: `"Good good. Sweet treat for both of us then."`, italic: true }],
  };

  return phases[phaseId] || [];
}
// ══════════════════════════════════════════════════════════════
// INTERACTIVE PICK UI — gender, School, deck
// ══════════════════════════════════════════════════════════════

function GenderPicker({ selected, onSelect }) {
  const opts = [
    { id: "m", label: "MALE",   icon: "♂" },
    { id: "f", label: "FEMALE", icon: "♀" },
  ];
  return (
    <div style={{ display: "flex", gap: 16, marginTop: 12 }}>
      {opts.map(o => {
        const isSelected = selected === o.id;
        return (
          <div key={o.id} onClick={() => onSelect(o.id)} style={{
            flex: 1, padding: "18px 12px", cursor: "pointer",
            textAlign: "center",
            background: isSelected
              ? `linear-gradient(135deg, ${GM.inkGold}33, ${GM.pageMid})`
              : `linear-gradient(135deg, rgba(0,0,0,0.25), ${GM.pageWarm})`,
            border: `1px solid ${isSelected ? GM.inkGold : GM.pageBorder}`,
            borderRadius: 3,
            boxShadow: isSelected
              ? `0 0 18px ${GM.inkGold}55, inset 0 0 10px ${GM.inkGold}22`
              : "inset 0 0 10px rgba(0,0,0,0.3)",
            transition: "all 0.25s ease",
          }}>
            <div style={{
              fontSize: 30, color: isSelected ? GM.inkGoldBright : GM.vellumDim,
              fontFamily: GM.font, marginBottom: 4,
              textShadow: isSelected ? `0 0 10px ${GM.inkGold}` : "none",
            }}>{o.icon}</div>
            <div style={{
              fontSize: 10, letterSpacing: 4, fontWeight: 900,
              color: isSelected ? GM.inkGoldBright : GM.vellumFaint,
            }}>{o.label}</div>
          </div>
        );
      })}
    </div>
  );
}

function SchoolPicker({ selected, onSelect }) {
  // The four SCHOOLS — the TOP-RIGHT stamp. Not Ilks.
  const schools = ["Space", "Phantom", "Time", "Dragon"];
  return (
    <div style={{
      display: "grid", gridTemplateColumns: "1fr 1fr",
      gap: 10, marginTop: 12,
    }}>
      {schools.map(school => {
        const accent = SCHOOL_ACCENT[school];
        const isSelected = selected === school;
        return (
          <div key={school} onClick={() => onSelect(school)} style={{
            padding: "14px 12px", cursor: "pointer",
            display: "flex", alignItems: "center", gap: 12,
            background: isSelected
              ? `linear-gradient(135deg, ${accent.tint}33, ${GM.pageMid})`
              : `linear-gradient(135deg, rgba(0,0,0,0.25), ${GM.pageWarm})`,
            border: `1px solid ${isSelected ? accent.tint : GM.pageBorder}`,
            borderRadius: 3,
            boxShadow: isSelected
              ? `0 0 20px ${accent.tint}66, inset 0 0 10px ${accent.tint}22`
              : "inset 0 0 10px rgba(0,0,0,0.3)",
            transition: "all 0.25s ease",
          }}>
            <SchoolSigil school={school} size={40} color={isSelected ? accent.tint : GM.vellumDim} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                fontSize: 13, fontWeight: 900, letterSpacing: 2,
                color: isSelected ? accent.glow : GM.vellumDim,
                textShadow: isSelected ? `0 0 8px ${accent.tint}` : "none",
              }}>◆ {school.toUpperCase()}</div>
              <div style={{
                fontSize: 10, fontStyle: "italic",
                color: isSelected ? GM.vellum : GM.vellumFaint,
                marginTop: 2,
              }}>{accent.label}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function DeckPicker({ school, selected, onSelect }) {
  // ⚠ THE FIELD IS `school` (2026-09-14). It was `ilk` and it held a
  // School the whole time. Left on `d.ilk` this returns ZERO decks and
  // a brand-new account cannot finish onboarding at all.
  const decks = STARTER_DECKS.filter(d => d.school === school);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 12 }}>
      {decks.map(d => {
        const isSelected = selected === d.id;
        return (
          <div key={d.id} onClick={() => onSelect(d.id)} style={{
            padding: "12px 14px", cursor: "pointer",
            background: isSelected
              ? `linear-gradient(135deg, ${d.color}44, ${GM.pageMid})`
              : `linear-gradient(135deg, rgba(0,0,0,0.25), ${GM.pageWarm})`,
            border: `1px solid ${isSelected ? d.color : GM.pageBorder}`,
            borderLeft: `3px solid ${d.color}`,
            borderRadius: 3,
            boxShadow: isSelected
              ? `0 0 16px ${d.color}55, inset 0 0 10px ${d.color}22`
              : "inset 0 0 10px rgba(0,0,0,0.3)",
            transition: "all 0.25s ease",
          }}>
            {/* ⭐ THE DECK BOX, 2026-08-22. This is a player's FIRST
                choice in the entire game — twelve decks, and until now
                nothing to look at while choosing. The box is small on
                purpose: the row still has to fit four of these in a
                phone-width column, so it reads at 52px or it does not
                ship. See StarterArt.js on why `plain` drops the collar
                at this size. */}
            <div style={{ display: "flex", gap: 11, alignItems: "center" }}>
              <StarterArt deck={d} w={38} h={53} plain dim={isSelected ? 1 : 0.7} style={{ flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontSize: 13, fontWeight: 900, fontStyle: "italic",
                  color: isSelected ? GM.vellum : GM.vellumDim,
                  fontFamily: GM.font, marginBottom: 3,
                }}>{d.name}</div>
                <div style={{
                  fontSize: 10, color: GM.vellumFaint, lineHeight: 1.4,
                  fontStyle: "italic",
                }}>
                  {/* Keep tagline short — first sentence of description only */}
                  {d.description?.split(".")[0]}.
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// ORDERED PHASE LIST — drives navigation
// One phase = one dialogue box. caP'n's V2 script.
// ══════════════════════════════════════════════════════════════
const PHASE_ORDER = [
  // ── ACT I — Willy alone, eager, on time ──────────────────────
  "w_there_you_are",
  "w_started_thinking",
  "w_goldfish",
  "w_welcome_willy",
  "w_enough_games",
  "w_where_was_i",

  // ── ACT II — V'ael arrives, interrupts ───────────────────────
  "v_acting_what",
  "v_your_who",
  "v_my_plaza",
  "beat_puppy_eyes",       // silent — Willy gives her the eyes
  "v_fine_boss_man",
  "v_dungeon_without",

  // ── ACT III — V'ael's intro + J'ynxi stakes ──────────────────
  "v_look_at_me",
  "v_my_name",
  "v_pierre",
  "v_meet_pierre",
  "v_reason_here",
  "v_jynxi_name",
  "v_walked_off",
  "v_call_of_duty",
  "v_everything_serves",
  "v_thread",

  // ── ACT IV — Gender pick ─────────────────────────────────────
  "v_before_further",
  "v_pick_no_agonize",
  "v_change_later",        // INTERACTIVE — gender picker on this phase
  "v_suits_you",
  "v_now_the_rest",
  "v_willys_time",

  // ── ACT V — Hand-off to Willy, V'ael exits ───────────────────
  "v_hand_back_willy",
  "v_take_through",
  "v_good_job",
  "beat_vael_struts",      // silent — V'ael leaves the stage

  // ── ACT VI — Willy alone beats ───────────────────────────────
  "beat_willy_stares",     // silent
  "beat_willy_dots1",      // silent
  "beat_willy_dots2",      // silent
  "p_didnt_care",
  "w_sweet_treat_want",
  "w_make_fast",
  "p_mmm_well",
  "w_mmm_yes_treat",
  "v_distant_tldr",        // distant — V'ael yells from down the hall
  "w_tealdiar_sorry",

  // ── ACT VII — Willy's tealdiar speed-run ─────────────────────
  "w_au_gratin",
  "w_unstable_world",
  "w_xaol_sweet_treat",
  "w_here_for_jynxi",
  "w_jynxi_plan",
  "w_steal_sol",
  "w_didnt_work",
  "w_terror_made",
  "w_seal_weakened",

  // ── ACT VIII — Parents reveal ────────────────────────────────
  "p_two_kids",
  "w_who_are_yo",
  "p_were_parents",
  "w_kings_grace",
  "p_find_in_plaza",
  "w_yes_thank_you",
  "p_sweet_treat",
  "w_ah",
  "w_mmm",

  // ── ACT IX — Required mechanical picks (School + deck) ───────
  // Wedged in here because finalize() needs both. The "real" tutorial
  // and rules tour move to the front-help-desk Plaza interaction
  // post-onboarding, per caP'n's design call.
  "w_one_more_thing",
  "w_pick_family_prompt",  // INTERACTIVE — School picker
  "w_school_chosen",
  "w_pick_deck_prompt",    // INTERACTIVE — deck picker
  "w_deck_chosen",         // FINAL — this is the curtain
];

// ══════════════════════════════════════════════════════════════
// SPEAKER PER PHASE — drives nameplate + dialogue accent color
// "willy" | "vael" | "player"
// ══════════════════════════════════════════════════════════════
const PHASE_SPEAKER = {
  // Act I
  w_there_you_are:    "willy",
  w_started_thinking: "willy",
  w_goldfish:         "willy",
  w_welcome_willy:    "willy",
  w_enough_games:     "willy",
  w_where_was_i:      "willy",
  // Act II
  v_acting_what:      "vael",
  v_your_who:         "vael",
  v_my_plaza:         "vael",
  beat_puppy_eyes:    "willy",   // silent beat, Willy's stage moment
  v_fine_boss_man:    "vael",
  v_dungeon_without:  "vael",
  // Act III
  v_look_at_me:       "vael",
  v_my_name:          "vael",
  v_pierre:           "vael",
  v_meet_pierre:      "vael",
  v_reason_here:      "vael",
  v_jynxi_name:       "vael",
  v_walked_off:       "vael",
  v_call_of_duty:     "vael",
  v_everything_serves:"vael",
  v_thread:           "vael",
  // Act IV
  v_before_further:   "vael",
  v_pick_no_agonize:  "vael",
  v_change_later:     "vael",
  v_suits_you:        "vael",
  v_now_the_rest:     "vael",
  v_willys_time:      "vael",
  // Act V
  v_hand_back_willy:  "vael",
  v_take_through:     "vael",
  v_good_job:         "vael",
  beat_vael_struts:   "vael",    // silent beat, V'ael's exit
  // Act VI
  beat_willy_stares:  "willy",
  beat_willy_dots1:   "willy",
  beat_willy_dots2:   "willy",
  p_didnt_care:       "player",
  w_sweet_treat_want: "willy",
  w_make_fast:        "willy",
  p_mmm_well:         "player",
  w_mmm_yes_treat:    "willy",
  v_distant_tldr:     "vael",    // distant flag handled in phase data
  w_tealdiar_sorry:   "willy",
  // Act VII
  w_au_gratin:        "willy",
  w_unstable_world:   "willy",
  w_xaol_sweet_treat: "willy",
  w_here_for_jynxi:   "willy",
  w_jynxi_plan:       "willy",
  w_steal_sol:        "willy",
  w_didnt_work:       "willy",
  w_terror_made:      "willy",
  w_seal_weakened:    "willy",
  // Act VIII
  p_two_kids:         "player",
  w_who_are_yo:       "willy",
  p_were_parents:     "player",
  w_kings_grace:      "willy",
  p_find_in_plaza:    "player",
  w_yes_thank_you:    "willy",
  p_sweet_treat:      "player",
  w_ah:               "willy",
  w_mmm:              "willy",
  // Act IX — required picks
  w_one_more_thing:   "willy",
  w_pick_family_prompt:"willy",
  w_school_chosen:       "willy",
  w_pick_deck_prompt: "willy",
  w_deck_chosen:      "willy",
};

// Phases that are silent beats (auto-advance, no nameplate, no continue button)
const SILENT_BEATS = new Set([
  "beat_puppy_eyes",
  "beat_vael_struts",
  "beat_willy_stares",
  "beat_willy_dots1",
  "beat_willy_dots2",
]);

// Phases where V'ael is offstage — figure hidden entirely.
// Starts when she struts off, never comes back this onboarding.
// (v_distant_tldr is during this offstage period — handled by `distant` flag.)
const VAEL_OFFSTAGE_FROM = "beat_vael_struts";

// Distant phases (speaker is offstage, dialogue shrinks/dims)
const DISTANT_PHASES = new Set([
  "v_distant_tldr",
]);

// Interactive phases — gender, School, deck pickers render inside the box
const GENDER_PICK_PHASE = "v_change_later";
const SCHOOL_PICK_PHASE    = "w_pick_family_prompt";
const DECK_PICK_PHASE   = "w_pick_deck_prompt";
const FINAL_PHASE       = "w_deck_chosen";

// Auto-advance delay for silent beats (ms)
const SILENT_BEAT_MS = 1600;

// ══════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ══════════════════════════════════════════════════════════════
export default function Onboarding({ uid, playerName, onComplete, devSkipEnabled = true }) {
  const [phaseIdx, setPhaseIdx] = useState(0);
  const [chosenGender, setChosenGender] = useState(null);
  const [chosenSchool, setChosenSchool] = useState(null);
  const [chosenDeck, setChosenDeck] = useState(null);
  const [saving, setSaving] = useState(false);
  const completedRef = useRef(false);

  // Pin the goldfish-joke variant once per session so it doesn't re-roll
  // every render. caP'n's note: "funny joke not same every time" — meaning
  // different per fresh onboarding, not per re-render of the same box.
  const goldfishLineRef = useRef(null);
  if (goldfishLineRef.current === null) {
    goldfishLineRef.current = getGoldfishJoke(playerName || "Aenarchist");
  }

  const phaseId = PHASE_ORDER[phaseIdx];
  const ctx = { playerName, chosenSchool, chosenDeck, goldfishLine: goldfishLineRef.current };

  // Phase data — supports either array shape (legacy) or { lines, silent, distant } shape
  const rawPhase = getPhaseLines(phaseId, ctx);
  const isObjectShape = rawPhase && !Array.isArray(rawPhase) && typeof rawPhase === "object";
  const lines    = isObjectShape ? rawPhase.lines : rawPhase;
  const isSilent = SILENT_BEATS.has(phaseId)  || (isObjectShape && rawPhase.silent);
  const isDistant= DISTANT_PHASES.has(phaseId) || (isObjectShape && rawPhase.distant);

  // V'ael is offstage from her exit beat onward (until end of onboarding)
  const vaelOffstageStartIdx = PHASE_ORDER.indexOf(VAEL_OFFSTAGE_FROM);
  const vaelOffstage = phaseIdx >= vaelOffstageStartIdx;

  const advance = useCallback(() => {
    setPhaseIdx(i => Math.min(i + 1, PHASE_ORDER.length - 1));
  }, []);

  // Auto-advance silent beats after a short pause
  useEffect(() => {
    if (!isSilent) return;
    const t = setTimeout(() => advance(), SILENT_BEAT_MS);
    return () => clearTimeout(t);
  }, [phaseId, isSilent, advance]);

  // ── Finalize: write profile fields, populate collection, call onComplete
  const finalize = useCallback(async () => {
    if (completedRef.current || saving) return;
    if (!uid || !chosenGender || !chosenDeck) return;
    completedRef.current = true;
    setSaving(true);
    try {
      // Write profile fields
      await fbUpdate(fbRef(db, `profiles/${uid}`), {
        gender: chosenGender,
        starterDeckId: chosenDeck,
        hasCompletedOnboarding: true,
      });
      // Populate collection from starter deck
      const deckData = getStarterDeck(chosenDeck);
      if (deckData) {
        // ⚠ EVERY LANE, NOT SOME. A starter deck has five: grimoire,
        // luxmoire, curios, esclings and prophecy. Seeding two of them
        // gave the player a collection that did not contain the deck
        // they play with. If a sixth lane is ever added, add it here —
        // tools/teachText.selftest.mjs asserts this list against the
        // lanes starterDecks.js actually ships.
        for (const cardId of Object.keys(deckData.grimoire || {})) {
          await addCardToCollection(uid, cardId, false);
        }
        for (const cardId of Object.keys(deckData.luxmoire || {})) {
          await addCardToCollection(uid, cardId, false);
        }
        for (const curioId of (deckData.curios || [])) {
          if (curioId) await addCardToCollection(uid, curioId, false);
        }
        for (const esclingId of (deckData.esclings || [])) {
          if (esclingId) await addCardToCollection(uid, esclingId, false);
        }
        if (deckData.prophecy) await addCardToCollection(uid, deckData.prophecy, false);
      }
    } catch (err) {
      console.error("[Onboarding] finalize error:", err);
    }
    setSaving(false);
    if (onComplete) onComplete({ deckId: chosenDeck, gender: chosenGender });
  }, [uid, chosenGender, chosenDeck, saving, onComplete]);

  // When we reach the final phase and tap continue, finalize.
  const handleContinue = useCallback(() => {
    if (phaseId === FINAL_PHASE) {
      finalize();
      return;
    }
    advance();
  }, [phaseId, finalize, advance]);

  // Dev skip — straight to the Plaza. Picks a sensible default starter so
  // downstream code (starter deck loading, collection, etc.) isn't broken.
  const handleSkip = useCallback(async () => {
    if (completedRef.current || saving) return;
    completedRef.current = true;
    setSaving(true);
    try {
      const fallbackDeck = STARTER_DECKS[0]?.id || null;
      await fbUpdate(fbRef(db, `profiles/${uid}`), {
        gender: chosenGender || "m",
        starterDeckId: chosenDeck || fallbackDeck,
        hasCompletedOnboarding: true,
      });
      if (!chosenDeck && fallbackDeck) {
        const deckData = getStarterDeck(fallbackDeck);
        if (deckData) {
          for (const cardId of Object.keys(deckData.grimoire || {})) {
            await addCardToCollection(uid, cardId, false);
          }
          for (const curioId of (deckData.curios || [])) {
            if (curioId) await addCardToCollection(uid, curioId, false);
          }
        }
      }
    } catch (err) {
      console.error("[Onboarding] skip error:", err);
    }
    setSaving(false);
    if (onComplete) onComplete({
      deckId: chosenDeck || STARTER_DECKS[0]?.id,
      gender: chosenGender || "m",
      skipped: true,
    });
  }, [uid, chosenGender, chosenDeck, saving, onComplete]);

  // Build the interactive content per phase
  const renderInteractive = () => {
    if (phaseId === GENDER_PICK_PHASE) {
      return (
        <>
          <GenderPicker selected={chosenGender} onSelect={setChosenGender} />
          {chosenGender && (
            <div style={{ textAlign: "right", marginTop: 12 }}>
              <button onClick={advance} style={ConfirmBtnStyle(GM.inkGold)}>◆ CONFIRM</button>
            </div>
          )}
        </>
      );
    }
    if (phaseId === SCHOOL_PICK_PHASE) {
      return (
        <>
          <SchoolPicker selected={chosenSchool} onSelect={setChosenSchool} />
          {chosenSchool && (
            <div style={{ textAlign: "right", marginTop: 12 }}>
              <button onClick={advance} style={ConfirmBtnStyle(SCHOOL_ACCENT[chosenSchool].tint)}>◆ CONFIRM</button>
            </div>
          )}
        </>
      );
    }
    if (phaseId === DECK_PICK_PHASE) {
      return (
        <>
          <DeckPicker school={chosenSchool} selected={chosenDeck} onSelect={setChosenDeck} />
          {chosenDeck && (
            <div style={{ textAlign: "right", marginTop: 12 }}>
              <button onClick={advance} style={ConfirmBtnStyle(SCHOOL_ACCENT[chosenSchool]?.tint || GM.inkGold)}>◆ CONFIRM</button>
            </div>
          )}
        </>
      );
    }
    return null;
  };

  const isInteractive = [GENDER_PICK_PHASE, SCHOOL_PICK_PHASE, DECK_PICK_PHASE].includes(phaseId);
  const isFinal = phaseId === FINAL_PHASE;

  const bgAccent = chosenSchool ? SCHOOL_ACCENT[chosenSchool] : null;
  const speaker = PHASE_SPEAKER[phaseId] || "willy";

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 500,
      overflow: "hidden", fontFamily: GM.font,
    }}>
      <style>{`
        @keyframes ob-slideUp {
          from { opacity: 0; transform: translate(-50%, 20px); }
          to   { opacity: 1; transform: translate(-50%, 0); }
        }
        @keyframes ob-moteDrift {
          0%   { transform: translateY(0) translateX(0); opacity: 0.3; }
          50%  { opacity: 0.8; }
          100% { transform: translateY(-100vh) translateX(40px); opacity: 0; }
        }
        @keyframes ob-starTwinkle {
          0%, 100% { opacity: 0.3; }
          50%      { opacity: 0.9; }
        }
        .ob-stars {
          position: absolute; inset: 0; pointer-events: none;
          background-image:
            radial-gradient(1px 1px at 10% 20%, ${GM.vellum}aa, transparent),
            radial-gradient(1px 1px at 25% 80%, ${GM.vellumDim}aa, transparent),
            radial-gradient(1px 1px at 40% 30%, ${GM.vellum}77, transparent),
            radial-gradient(1px 1px at 55% 60%, ${GM.vellumDim}aa, transparent),
            radial-gradient(1.5px 1.5px at 70% 15%, ${GM.inkGoldBright}88, transparent),
            radial-gradient(1px 1px at 85% 75%, ${GM.vellum}77, transparent),
            radial-gradient(1px 1px at 92% 40%, ${GM.vellumDim}aa, transparent),
            radial-gradient(1px 1px at 15% 55%, ${GM.vellum}66, transparent),
            radial-gradient(1px 1px at 65% 35%, ${GM.vellumDim}99, transparent),
            radial-gradient(1px 1px at 35% 90%, ${GM.vellum}88, transparent);
          animation: ob-starTwinkle 4s ease-in-out infinite;
        }
        .ob-motes {
          position: absolute; inset: 0; pointer-events: none;
          background-image:
            radial-gradient(2px 2px at 20% 90%, ${GM.inkGold}, transparent 50%),
            radial-gradient(1.5px 1.5px at 60% 95%, ${GM.inkGoldBright}, transparent 50%),
            radial-gradient(2px 2px at 80% 85%, ${GM.inkGoldDim}, transparent 50%),
            radial-gradient(1.5px 1.5px at 40% 90%, ${GM.inkGold}, transparent 50%);
          animation: ob-moteDrift 12s linear infinite;
        }
        @keyframes ob-fadeIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        .ob-fadein { animation: ob-fadeIn 0.8s ease-out; }
      `}</style>

      <Backdrop schoolAccent={bgAccent} />

      {/* Stage figures — V'ael hides entirely once she struts off */}
      <SpeakerStage activeSpeaker={speaker} vaelOffstage={vaelOffstage} />

      {/* Centered ambient illustration zone — top half of screen */}
      <div className="ob-fadein" key={phaseId} style={{
        position: "absolute", top: "6vh", left: "50%", transform: "translateX(-50%)",
        width: "min(90vw, 720px)", textAlign: "center",
        pointerEvents: "none",
        zIndex: 2,
      }}>
        {phaseId === "v_acting_what" && (
          <div style={{
            fontSize: 12, letterSpacing: 8, color: "#ff7a4a", fontWeight: 900,
            marginTop: "4vh",
            textShadow: "0 0 16px #d44a2a, 0 0 6px #fff",
          }}>◆ SHE ARRIVES ◆</div>
        )}
        {phaseId === SCHOOL_PICK_PHASE && (
          <div style={{
            fontSize: 12, letterSpacing: 6, color: GM.inkGold, fontWeight: 900,
            marginTop: "4vh",
          }}>◆ THE FOUR FAMILIES ◆</div>
        )}
        {phaseId === "w_school_chosen" && chosenSchool && (
          <div style={{ marginTop: "4vh" }}>
            <SchoolSigil school={chosenSchool} size={100} color={SCHOOL_ACCENT[chosenSchool].tint} />
            <div style={{
              fontSize: 24, fontWeight: 900, fontStyle: "italic",
              color: SCHOOL_ACCENT[chosenSchool].glow, fontFamily: GM.font,
              textShadow: `0 0 20px ${SCHOOL_ACCENT[chosenSchool].tint}`,
              marginTop: 8, letterSpacing: 3,
            }}>{chosenSchool.toUpperCase()}</div>
            <div style={{
              fontSize: 11, color: GM.vellumFaint, fontStyle: "italic",
              letterSpacing: 3, marginTop: 2,
            }}>― {SCHOOL_ACCENT[chosenSchool].label} ―</div>
          </div>
        )}
        {phaseId === "w_deck_chosen" && chosenDeck && (
          <div style={{ marginTop: "4vh" }}>
            <div style={{
              fontSize: 22, fontWeight: 900, fontStyle: "italic",
              color: GM.vellum, fontFamily: GM.font,
              textShadow: `0 0 20px ${STARTER_DECKS.find(d => d.id === chosenDeck)?.color || GM.inkGold}`,
              letterSpacing: 2,
            }}>{STARTER_DECKS.find(d => d.id === chosenDeck)?.name}</div>
          </div>
        )}
      </div>

      {/* Dialogue */}
      <DialogueBox
        lines={lines}
        onContinue={(isInteractive || isSilent) ? null : handleContinue}
        continueLabel={isFinal ? "→ Enter the Plaza" : "→"}
        speaker={speaker}
        distant={isDistant}
        silent={isSilent}
      >
        {renderInteractive()}
      </DialogueBox>

      {/* Dev skip button — top right, hidden in production via devSkipEnabled prop */}
      {devSkipEnabled && (
        <button onClick={handleSkip} style={{
          position: "absolute", top: 12, right: 12, zIndex: 10,
          background: "transparent",
          border: `1px solid ${GM.inkBrown}66`,
          color: GM.vellumFaint,
          fontSize: 9, letterSpacing: 2, padding: "4px 10px",
          borderRadius: 2, cursor: "pointer",
          fontFamily: GM.font,
          opacity: 0.6,
        }}>SKIP INTRO</button>
      )}

      {/* Phase progress dots (top center, subtle) */}
      <div style={{
        position: "absolute", top: 12, left: "50%", transform: "translateX(-50%)",
        display: "flex", gap: 5, zIndex: 5,
      }}>
        {PHASE_ORDER.map((_, i) => (
          <div key={i} style={{
            width: i === phaseIdx ? 16 : 4,
            height: 4, borderRadius: 2,
            background: i <= phaseIdx ? GM.inkGold : GM.pageBorder,
            opacity: i <= phaseIdx ? 0.8 : 0.4,
            transition: "all 0.3s ease",
          }} />
        ))}
      </div>
    </div>
  );
}

// ── Small reusable confirm button style ────────────────────────
function ConfirmBtnStyle(color) {
  return {
    background: `linear-gradient(135deg, ${color}44, ${GM.pageMid})`,
    border: `1px solid ${color}`,
    borderRadius: 3, cursor: "pointer",
    color: color, fontSize: 11, fontFamily: GM.font,
    fontWeight: 900, padding: "8px 18px", letterSpacing: 3,
    boxShadow: `0 0 14px ${color}55`,
  };
}
